import { Injectable, OnDestroy, effect, NgZone } from '@angular/core';
import mqtt from 'mqtt';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { MQTT_CONSTANTS, MQTT_CONNECTION_OPTIONS } from './mqtt.config';
import { OmniRequest, OmniResponse, ControlAction, DeviceFilter, RawDevice, DeviceTable, ParsedDevice, AlarmMessage, RawAlarmDevice } from './omni-protocol.interface';
import { UserService } from '../services/user.service';
import { AuthenticateService } from '../services/authenticate.service';
import {
  AC_ID_MAPPING,
  AC_NAME_MAPPING,
  POWER_ID_MAPPING,
  POWER_NAME_MAPPING,
  LIGHT_ID_MAPPING,
  LIGHT_NAME_MAPPING,
  RB_NAME_MAPPING,
  ROOM_SYSTEM_MAPPING,
  CUSTOM_DEVICE_ID_MAPPING,
  FAN_ID_MAPPING,
  FAN_NAME_MAPPING,
  DOOR_ID_MAPPING,
  DOOR_NAME_MAPPING
} from './omni-mqtt-mapping.data';

const getLogTime = () => `[${new Date().toLocaleString('zh-TW', { hour12: false })}]`;

@Injectable({
  providedIn: 'root'
})
export class OmniMqttService implements OnDestroy {

  private client: mqtt.MqttClient | null = null;
  private clientId: string | null = null;
  private userUuid: string | null = null;
  private lastToken: string | null = null;

  private isConnectedSubject = new BehaviorSubject<boolean>(false);
  public isConnected$ = this.isConnectedSubject.asObservable();

  private messageSubject = new Subject<OmniResponse>();
  public messages$ = this.messageSubject.asObservable();

  // 🏆 取代原本的 RawDevice[] 陣列，改用「字典物件 Table」管理狀態！
  private deviceTableSubject = new BehaviorSubject<DeviceTable>({});
  public deviceTable$ = this.deviceTableSubject.asObservable();

  // 🚀 即時告警推播 (alarm.changed)
  private alarmSubject = new Subject<AlarmMessage>();
  public alarms$ = this.alarmSubject.asObservable();

  // 📋 告警列表 (system.get_status + objName:ALARM 主動查詢)
  private alarmListSubject = new BehaviorSubject<RawAlarmDevice[]>([]);
  public alarmList$ = this.alarmListSubject.asObservable();

  constructor(
    private userService: UserService,
    private authService: AuthenticateService,
    private zone: NgZone
  ) {
    effect(() => {
      const user = this.userService.currentUser();
      const token = this.authService.accessToken();

      if (user?.uuid && token) {
        // 如果 client 尚未建立，或 User 變了，或 Token 變了，就重連
        if (!this.client || this.userUuid !== user.uuid || this.lastToken !== token) {
          this.lastToken = token;
          this.reconnect(user.uuid);
        }
      } else {
        this.disconnect();
      }
    });
  }

  private reconnect(newId: string) {
    this.disconnect();
    this.userUuid = newId;
    // 在 UUID 後面增加三位隨機亂碼
    const randomSuffix = Math.random().toString(36).substring(2, 5);
    this.clientId = `${newId}-${randomSuffix}`;
    this.connect();
  }

  private disconnect() {
    if (this.client) {
      this.client.end();
      this.client = null;
    }
    this.clientId = null;
    this.isConnectedSubject.next(false);
  }

  private connect() {
    if (!this.clientId) return;

    console.log(`[MQTT] ${getLogTime()} 正在連線 ID: ${this.clientId}`);
    const user = this.userService.currentUser();
    const token = localStorage.getItem('access_token') || '';

    const options = {
      ...MQTT_CONNECTION_OPTIONS,
      clientId: this.clientId,
      username: user?.empId,
      password: token
    };
    console.log('options', options)
    const clientInstance = mqtt.connect(options);
    this.client = clientInstance;

    clientInstance.on('connect', () => {
      if (this.client !== clientInstance) return;
      this.zone.run(() => {
        this.handleConnect();
      });
    });
    clientInstance.on('error', (err) => {
      if (this.client !== clientInstance) return;
      console.error(`[MQTT] ${getLogTime()} 錯誤:`, err);
    });
    clientInstance.on('close', () => {
      if (this.client !== clientInstance) return;
      console.log(`[MQTT] ${getLogTime()} 連線已關閉 (close)`);
      this.zone.run(() => {
        this.isConnectedSubject.next(false);
      });
    });
    clientInstance.on('message', (topic, payload) => {
      if (this.client !== clientInstance) return;
      this.zone.run(() => {
        this.handleMessage(topic, payload);
      });
    });
  }

  private handleConnect() {
    console.log(`[MQTT] ${getLogTime()} 已連線`);
    this.isConnectedSubject.next(true);
    this.initSubscriptions();

    // 預設抓取測試機資料
    if (MQTT_CONSTANTS.OMNI_ID) {
      this.getConfig(MQTT_CONSTANTS.OMNI_ID);
      this.getStatus(MQTT_CONSTANTS.OMNI_ID);
      this.getAlarmStatus(MQTT_CONSTANTS.OMNI_ID);
      this.getControlLog(MQTT_CONSTANTS.OMNI_ID);
    }
  }

  private handleMessage(topic: string, payload: Buffer) {
    try {
      const data: any = JSON.parse(payload.toString());
      console.log(`[MQTT] ${getLogTime()} 收到來自主題 [${topic}] 的封包:`, data);

      // 攔截 CONTROL_LOG 封包
      if (data.objName === 'CONTROL_LOG' && data.devices && Array.isArray(data.devices)) {
        console.log(`[MQTT] ${getLogTime()} 收到 CONTROL_LOG 封包並將其轉換合進 mergetable:`, data);
        this.mergeToTable(this.buildRawDevicesFromControlLog(data), true);
        return;
      }

      // 攔截告警封包
      if (data.cmd === 'alarm.changed') {
        console.log(`[MQTT] ${getLogTime()} 收到告警封包`, data);
        this.alarmSubject.next(data as AlarmMessage);
        return;
      }

      // 如果有 devices 陣列
      if (data.devices && Array.isArray(data.devices)) {
        // 判斷是否全為告警資料 (ALARM list response)
        if (data.devices.length > 0 && data.devices[0].objName === 'ALARM') {
          this.alarmListSubject.next(data.devices as RawAlarmDevice[]);
          console.log(`[MQTT] ${getLogTime()} 告警列表已更新:`, data.devices);
        } else {
          this.mergeToTable(data.devices);
        }
      }
      // 處理單一設備增量更新 (無 devices 陣列，直接附帶 objName 跟 id)
      else if (data.objName && data.id) {
        this.mergeToTable([data as RawDevice]);
      }

      this.messageSubject.next(data);
    } catch (e) {
      console.error(`[MQTT] ${getLogTime()} 處理訊息時發生錯誤:`, e);
      console.warn(`[MQTT] ${getLogTime()} 原始訊息:`, payload.toString());
    }
  }

  /**
   * 將 CONTROL_LOG 封包的 devices 組裝成 RawDevice[] 供 mergeToTable 使用
   */
  private buildRawDevicesFromControlLog(data: any): RawDevice[] {
    const allStatusLines: string[] = [];

    data.devices.forEach((dev: any) => {
      if (!Array.isArray(dev.status)) return;
      dev.status.forEach((line: string) => {
        const parts = line.split('|');
        if (parts.length < 2) {
          allStatusLines.push(line);
          return;
        }

        let objName = parts[0];
        let id = parts[1];

        // SYS + scenes/schedules/smart → SCENES/SCHEDULES/SMART + ""
        if (objName.toUpperCase() === 'SYS') {
          const idLower = id.toLowerCase();
          if (idLower === 'scenes') { objName = 'SCENES'; id = ''; }
          else if (idLower === 'schedules') { objName = 'SCHEDULES'; id = ''; }
          else if (idLower === 'smart') { objName = 'SMART'; id = ''; }
        }

        parts[0] = objName;
        parts[1] = id;
        allStatusLines.push(parts.join('|'));
      });
    });

    // 依 objName|id 分組
    const groups: Record<string, string[]> = {};
    allStatusLines.forEach(line => {
      const parts = line.split('|');
      if (parts.length < 2) return;
      const key = `${parts[0]}|${parts[1]}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(line);
    });

    return Object.entries(groups).map(([key, lines]) => {
      const [objName, id] = key.split('|');
      return { objName, id, status: lines };
    });
  }

  // --- 設備映射配置區 ---
  private readonly AC_ID_MAPPING = AC_ID_MAPPING;
  private readonly AC_NAME_MAPPING = AC_NAME_MAPPING;
  private readonly POWER_ID_MAPPING = POWER_ID_MAPPING;
  private readonly POWER_NAME_MAPPING = POWER_NAME_MAPPING;
  private readonly LIGHT_ID_MAPPING = LIGHT_ID_MAPPING;
  private readonly LIGHT_NAME_MAPPING = LIGHT_NAME_MAPPING;
  private readonly FAN_ID_MAPPING = FAN_ID_MAPPING;
  private readonly FAN_NAME_MAPPING = FAN_NAME_MAPPING;
  private readonly DOOR_ID_MAPPING = DOOR_ID_MAPPING;
  private readonly DOOR_NAME_MAPPING = DOOR_NAME_MAPPING;
  private readonly RB_NAME_MAPPING = RB_NAME_MAPPING;
  private readonly ROOM_SYSTEM_MAPPING = ROOM_SYSTEM_MAPPING;
  private readonly CUSTOM_DEVICE_ID_MAPPING = CUSTOM_DEVICE_ID_MAPPING;

  /**
   * 🚀 核心邏輯：合併 MQTT 資料並進行設備拆分映射
   * 各階段處理邏輯已抽離至獨立私有方法以提升可讀性。
   */
  private mergeToTable(incomingList: RawDevice[], isControlLog = false) {
    const table = { ...this.deviceTableSubject.value };

    const deviceStatusRawList = incomingList.filter(raw => raw.objName === 'DEVICE');
    const normalList = incomingList.filter(raw => raw.objName !== 'DEVICE');

    normalList.forEach(raw => {
      const rawId = raw.id || '';
      const rawNumId = parseInt(rawId);
      const isAcModule = raw.objName === 'MODULE' && ['3', '4', '5'].includes(rawId);
      const roomMap = (raw.objName === 'MODBUS' && rawNumId >= 12 && rawNumId <= 22)
        ? this.ROOM_SYSTEM_MAPPING[rawId]
        : null;

      const integratedData = this.buildIntegratedData(raw, isControlLog);

      if (!isAcModule && !roomMap) this.processStageA(table, raw, integratedData);
      if (isAcModule) this.processStageB_AcModule(table, raw, integratedData);
      if (roomMap) this.processStageC_RoomSystem(table, raw, integratedData, roomMap);
    });

    // 為 HOST / ONVIF(CCTV) 的 Online 點位補充名稱（status 會送但 config 不含）
    const ONLINE_POINT_TYPES = new Set(['HOST', 'ONVIF']);
    Object.values(table).forEach(device => {
      if (!ONLINE_POINT_TYPES.has(device.type)) return;
      if (device.data['Online'] && !device.data['Online'].name) {
        device.data['Online'].name = '設備運轉狀態';
        device.data['Online'].id = 'Online';
      }
      if (device.data['Online'] && !device.config?.find((c: any) => c.id === 'Online')) {
        device.config!.push({ id: 'Online', name: '設備運轉狀態', type: 0, range: [0, '~', 1] } as any);
      }
    });

    if (deviceStatusRawList.length > 0) this.processDeviceStatusList(table, deviceStatusRawList);

    this.deviceTableSubject.next(table);
    console.log(`[MQTT] ${getLogTime()} Device Table Updated:`, table);
  }

  /**
   * 解析 pipe 分隔的 status 字串為結構化物件
   * 格式：ObjName|id|funId|value|timestamp
   */
  private parseStatusLine(line: string): { rawObjName: string; rawId: string; funId: string; rawValue: string; timestamp: number } | null {
    const parts = line.split('|');
    if (parts.length < 4) return null;
    return {
      rawObjName: parts[0],
      rawId: parts[1],
      funId: parts[2],
      rawValue: parts[3],
      timestamp: parts[4] ? parseInt(parts[4]) : Date.now()
    };
  }

  /**
   * 將 raw.config 與 raw.status 整合成 integratedData 字典
   * isControlLog = true 時只取操作者姓名，不更新設備狀態值
   */
  private buildIntegratedData(raw: RawDevice, isControlLog: boolean): Record<string, any> {
    const integratedData: Record<string, any> = {};

    if (Array.isArray(raw.config)) {
      raw.config.forEach((c: any) => {
        integratedData[c.id] = { ...c, rawObjName: raw.objName, rawId: raw.id, rawTargetId: c.id };
      });
    }

    if (Array.isArray(raw.status)) {
      raw.status.forEach(str => {
        const parsed = this.parseStatusLine(str);
        if (!parsed) return;

        const { funId, rawValue, timestamp } = parsed;
        let operator: string | null = null;
        let val: any = undefined;

        if (isControlLog) {
          operator = rawValue.includes(',') ? rawValue.split(',')[1] : rawValue;
        } else {
          val = rawValue;
          if (val !== '' && !isNaN(Number(val))) val = Number(val);
          else if (val === 'null') val = null;
        }

        if (!integratedData[funId]) {
          integratedData[funId] = { rawObjName: raw.objName, rawId: raw.id, rawTargetId: funId };
        }

        if (isControlLog) {
          const existingTime = integratedData[funId].updateTimestamp || 0;
          if (timestamp >= existingTime) {
            integratedData[funId].updateTimestamp = timestamp;
            if (operator !== null) integratedData[funId].operator = operator;
          }
        } else {
          const existingTime = integratedData[funId].timestamp || 0;
          if (timestamp >= existingTime) {
            integratedData[funId].value = val;
            integratedData[funId].timestamp = timestamp;
          }
        }
      });
    }

    return integratedData;
  }

  /**
   * 階段 A：基本設備與簡易映射（Power / Light / Fan / Door / AirQuality）
   */
  private processStageA(table: DeviceTable, raw: RawDevice, integratedData: Record<string, any>) {
    const rawId = raw.id || '';
    const targetPowerId = raw.objName === 'MODBUS' ? this.POWER_ID_MAPPING[rawId] : null;
    const targetLightId = raw.objName === 'MODULE' ? this.LIGHT_ID_MAPPING[rawId] : null;
    const targetFanId = raw.objName === 'MODBUS' ? this.FAN_ID_MAPPING[rawId] : null;
    const targetDoorId = raw.objName === 'MODBUS' ? this.DOOR_ID_MAPPING[rawId] : null;

    let uuid = `${raw.objName}_${rawId}`;
    let type = raw.objName;
    let id = rawId;
    let name = '';

    switch (true) {
      case !!targetPowerId:
        id = targetPowerId; uuid = `POWER_${id}`; type = 'POWER'; name = this.POWER_NAME_MAPPING[id] || ''; break;
      case !!targetLightId:
        id = targetLightId; uuid = `LIGHT_${id}`; type = 'LIGHT'; name = this.LIGHT_NAME_MAPPING[id] || ''; break;
      case !!targetFanId:
        id = targetFanId; uuid = `FAN_${id}`; type = 'FAN'; name = this.FAN_NAME_MAPPING[id] || ''; break;
      case !!targetDoorId:
        id = targetDoorId; uuid = `DOOR_${id}`; type = 'DOOR'; name = this.DOOR_NAME_MAPPING[id] || ''; break;
      case raw.objName === 'MODBUS' && rawId === '9':
        id = '29'; uuid = `AIR_QUALITY_${id}`; type = 'AIR_QUALITY'; break;
    }

    this.updateNodeWithIntegratedData(table, uuid, raw, type, id, name, integratedData);
  }

  /**
   * 階段 B：冷氣模組拆解（MODULE 3/4/5）
   */
  private processStageB_AcModule(table: DeviceTable, raw: RawDevice, integratedData: Record<string, any>) {
    const acFunMap: Record<string, string> = {
      'power': 'Power', 'mode': 'Mode', 'settemp': 'SetTemp',
      'swing': 'Swing', 'fanspeed': 'FanSpeed', 'errorstatus': 'ErrorStatus', 'roomtemp': 'RoomTemp'
    };

    Object.entries(integratedData).forEach(([fullFunId, item]) => {
      const underscoreIdx = fullFunId.lastIndexOf('_');
      if (underscoreIdx === -1) return;

      const rawFunId = fullFunId.substring(0, underscoreIdx);
      const subId = fullFunId.substring(underscoreIdx + 1);
      const funId = acFunMap[rawFunId.toLowerCase()] || (rawFunId.charAt(0).toUpperCase() + rawFunId.slice(1));
      const cleanFullFunId = `${funId}_${subId}`;

      const targetAcId = this.AC_ID_MAPPING[`${raw.id}_${subId}`];
      if (!targetAcId) return;

      const acUuid = `AC_${targetAcId}`;
      this.ensureNode(table, acUuid, 'AC', targetAcId, this.AC_NAME_MAPPING[targetAcId], raw.online === 1);
      table[acUuid].acSubId = subId;

      let cleanName = item.name || '';
      if (cleanName && cleanName.includes('_')) cleanName = cleanName.substring(cleanName.indexOf('_') + 1);

      this.assignWithTimestampCheck(table[acUuid].data, cleanFullFunId, item, { id: cleanFullFunId, name: cleanName });

      // 為虛擬節點補充 config 以利相容
      if (item.name) {
        const newConfigItem = { ...item, id: cleanFullFunId, name: cleanName };
        const existConf = table[acUuid].config!.find((c: any) => c.id === cleanFullFunId);
        if (existConf) Object.assign(existConf, newConfigItem);
        else table[acUuid].config!.push(newConfigItem);
      }
    });
  }

  /**
   * 階段 C：會議室系統拆解（Host / Panel / RB 捲簾）
   */
  private processStageC_RoomSystem(table: DeviceTable, raw: RawDevice, integratedData: Record<string, any>, roomMap: any) {
    const PANEL_FUN_IDS = new Set(['01_1', '01_3']);
    const RB_FUN_IDS = new Set(['01_2', '01_4']);
    const HOST_EXCLUDED_FUN_IDS = new Set([...PANEL_FUN_IDS, ...RB_FUN_IDS]);

    // 先建立 HOST 節點（避免迴圈內重複呼叫 ensureNode）
    const hostUuid = `HOST_${roomMap.host}`;
    this.ensureNode(table, hostUuid, 'HOST', roomMap.host, roomMap.name.replace('會議室', '環控主機'), raw.online === 1);

    Object.entries(integratedData).forEach(([funId, item]) => {
      // HOST — 排除已分配給 PANEL / RB 的 funId
      if (!HOST_EXCLUDED_FUN_IDS.has(funId)) {
        this.assignWithTimestampCheck(table[hostUuid].data, funId, item);
        if (item.name) {
          const existConf = table[hostUuid].config!.find((c: any) => c.id === funId);
          if (existConf) Object.assign(existConf, item);
          else table[hostUuid].config!.push({ ...item });
        }
      }

      // PANEL（01_1 會議室使用狀態 / 01_3 面板通訊狀態）
      if (PANEL_FUN_IDS.has(funId)) {
        const panelUuid = `PANEL_${roomMap.panel}`;
        this.ensureNode(table, panelUuid, 'PANEL', roomMap.panel, roomMap.name.replace('會議室', '會議室面板'), raw.online === 1);
        this.assignWithTimestampCheck(table[panelUuid].data, funId, item);
        if (item.name) {
          const existConf = table[panelUuid].config!.find((c: any) => c.id === funId);
          if (existConf) Object.assign(existConf, item);
          else table[panelUuid].config!.push({ ...item });
        }
      }

      // RB 捲簾（01_2 窗簾狀態 / 01_4 窗簾通訊狀態）
      if (RB_FUN_IDS.has(funId)) {
        roomMap.rb.forEach((rbId: string) => {
          const rbUuid = `RB_${rbId}`;
          const rbName = this.RB_NAME_MAPPING[rbId] || `捲簾_${rbId}`;
          this.ensureNode(table, rbUuid, 'RB', rbId, rbName, raw.online === 1);
          this.assignWithTimestampCheck(table[rbUuid].data, funId, item);
          if (item.name) {
            const existConf = table[rbUuid].config!.find((c: any) => c.id === funId);
            if (existConf) Object.assign(existConf, item);
            else table[rbUuid].config!.push({ ...item });
          }
        });
      }
    });
  }

  /**
   * 處理 DEVICE objName 的狀態更新（全設備或單一設備的正常/異常/維修/報廢）
   */
  private processDeviceStatusList(table: DeviceTable, deviceStatusRawList: RawDevice[]) {
    const deviceIdToUuid = new Map<string, string>();
    Object.values(table).forEach(device => {
      if (device.deviceId) deviceIdToUuid.set(device.deviceId, device.uuid);
    });

    const clonedUuids = new Set<string>();

    deviceStatusRawList.forEach(raw => {
      if (!Array.isArray(raw.status)) return;
      raw.status.forEach((str: string) => {
        const parsed = this.parseStatusLine(str);
        if (!parsed) return;

        const { rawId: deviceId, funId, rawValue, timestamp } = parsed;
        let val: any = rawValue;
        if (val !== '' && !isNaN(Number(val))) val = Number(val);

        const uuid = deviceIdToUuid.get(deviceId);
        if (!uuid || !table[uuid]) return;

        // 重建參照以觸發 Angular signal 變更偵測
        if (!clonedUuids.has(uuid)) {
          table[uuid] = { ...table[uuid], data: { ...table[uuid].data } };
          clonedUuids.add(uuid);
        }

        if (!table[uuid].data[funId]) {
          table[uuid].data[funId] = { value: val, timestamp, rawObjName: 'DEVICE', rawId: raw.id, rawTargetId: funId };
        } else {
          const existingTime = table[uuid].data[funId].timestamp || 0;
          if (timestamp >= existingTime) {
            table[uuid].data[funId].value = val;
            table[uuid].data[funId].timestamp = timestamp;
          }
        }
      });
    });
  }

  /**
   * 輔助工具：確保節點存在，若不存在則初始化
   */
  private ensureNode(table: any, uuid: string, type: string, id: string, name: string, isOnline: boolean) {
    if (!table[uuid]) {
      table[uuid] = {
        uuid,
        type,
        id,
        name,
        deviceId: this.CUSTOM_DEVICE_ID_MAPPING[name] || '',
        isOnline,
        data: {},
        config: []
      };
    } else {
      // 🔑 重建物件參照，確保 BehaviorSubject.next() 後 Angular signal 能偵測到變化
      table[uuid] = { ...table[uuid], isOnline };
      table[uuid].data = { ...table[uuid].data };
      table[uuid].config = [...(table[uuid].config || [])];
      if (!table[uuid].deviceId && table[uuid].name) {
        table[uuid].deviceId = this.CUSTOM_DEVICE_ID_MAPPING[table[uuid].name] || '';
      }
    }
  }

  /**
   * 以已整合的數據直接更新節點 (Stage A 用)
   */
  private updateNodeWithIntegratedData(table: DeviceTable, uuid: string, raw: RawDevice, type: string, id: string, name: string, integratedData: Record<string, any>) {
    const finalName = name || raw.name || '';
    const existing: ParsedDevice = table[uuid] ? { ...table[uuid], data: { ...table[uuid].data }, config: [...(table[uuid].config || [])] } : {
      uuid,
      type,
      id,
      name: finalName,
      deviceId: this.CUSTOM_DEVICE_ID_MAPPING[finalName] || '',
      isOnline: raw.online === 1,
      data: {},
      config: Array.isArray(raw.config) ? raw.config : []
    };

    if (Array.isArray(raw.config)) {
      existing.config = raw.config;
      if (raw.name && !name) existing.name = raw.name;
    }
    if (raw.online !== undefined) existing.isOnline = raw.online === 1;

    // 防待處理：若剛建立時 name 為空字串導致沒抓到 ID，更新時再抓一次
    if (!existing.deviceId && existing.name) {
      existing.deviceId = this.CUSTOM_DEVICE_ID_MAPPING[existing.name] || '';
    }

    // 將統合好的 integratedData 搬入 data
    Object.keys(integratedData).forEach(key => {
      this.assignWithTimestampCheck(existing.data, key, integratedData[key]);
    });

    table[uuid] = existing;
  }

  private assignWithTimestampCheck(targetNodeData: Record<string, any>, key: string, incomingItem: any, extraFields?: any) {
    if (!targetNodeData[key]) {
      targetNodeData[key] = {} as any;
      Object.assign(targetNodeData[key], incomingItem, extraFields);
    } else {
      const existing = targetNodeData[key];

      // 始終更新/同步靜態與組態欄位
      const configFields = ['name', 'unit', 'range', 'type', 'tag', 'sourceKey', 'rawObjName', 'rawId', 'rawTargetId'];
      configFields.forEach(f => {
        if (incomingItem[f] !== undefined) {
          existing[f] = incomingItem[f];
        }
      });
      if (extraFields) {
        Object.assign(existing, extraFields);
      }

      // 1. 採樣時間/即時狀態更新
      if (incomingItem.timestamp !== undefined) {
        const incomingTime = incomingItem.timestamp || 0;
        const existingTime = existing.timestamp || 0;
        if (incomingTime >= existingTime) {
          existing.value = incomingItem.value;
          existing.timestamp = incomingTime;
        }
      }

      // 2. 控制日誌更新時間更新
      if (incomingItem.updateTimestamp !== undefined) {
        const incomingTime = incomingItem.updateTimestamp || 0;
        const existingTime = existing.updateTimestamp || 0;
        if (incomingTime >= existingTime) {
          existing.updateTimestamp = incomingTime;
          if (incomingItem.operator !== undefined) {
            existing.operator = incomingItem.operator;
          }
        }
      }
    }
  }


  /**
   * 取得快取的設備列表清單 (轉換回 Array)
   */
  public getDevices(filter?: DeviceFilter): ParsedDevice[] {
    let list = Object.values(this.deviceTableSubject.value);
    if (!filter) return list;

    if (filter.type) {
      const types = Array.isArray(filter.type) ? filter.type : [filter.type];
      list = list.filter(d => types.includes(d.type));
    }
    if (filter.onlineOnly) {
      list = list.filter(d => d.isOnline);
    }
    return list;
  }

  private initSubscriptions() {
    if (!this.clientId) return;
    // 訂閱個人頻道
    this.subscribeTopic(`${MQTT_CONSTANTS.TOPIC_PREFIX.CLIENT}/${this.clientId}`);
    // 訂閱主機廣播頻道
    if (MQTT_CONSTANTS.OMNI_ID) {
      this.subscribeTopic(`${MQTT_CONSTANTS.TOPIC_PREFIX.CLIENT}/${MQTT_CONSTANTS.OMNI_ID}`);
    }
  }

  public subscribeTopic(topic: string) {
    if (!this.client) return;
    this.client.subscribe(topic, (err) => {
      if (err) console.error(`[MQTT] ${getLogTime()} 訂閱失敗: ${topic}`, err);
      else console.log(`[MQTT] ${getLogTime()} 已訂閱主題: ${topic}`);
    });
  }

  // --- 指令發送方法 ---

  public getConfig(omniId: string) {
    this.publishToOmni(omniId, { cmd: 'system.get_config' });
  }

  public getStatus(omniId: string) {
    this.publishToOmni(omniId, { cmd: 'system.get_status' });
  }

  public getAlarmStatus(omniId: string) {
    this.publishToOmni(omniId, {
      cmd: 'system.get_status',
      objName: 'ALARM'
    });
  }

  public getControlLog(omniId: string) {
    this.publishToOmni(omniId, {
      cmd: 'system.get_status',
      objName: 'CONTROL_LOG'
    });
  }

  public sendControl(omniId: string, actions: ControlAction[]) {
    const actionStrings = actions.map(a => `${a.objName}|${a.id}|${a.funId}|${a.value}`);
    this.publishToOmni(omniId, {
      cmd: 'device.control',
      payload: { actions: actionStrings }
    });
  }

  private publishToOmni(omniId: string, baseReq: { cmd: string; objName?: string; payload?: any }) {
    if (!this.client?.connected) return;

    const topic = `${MQTT_CONSTANTS.TOPIC_PREFIX.OMNI}/${omniId}`;
    const token = localStorage.getItem('access_token') || '';

    const fullReq: OmniRequest = {
      cid: this.clientId || `CID_${Date.now()}`,
      timestamp: Date.now(),
      token: token,
      cmd: baseReq.cmd,
      ...(baseReq.objName && { objName: baseReq.objName }),
      payload: baseReq.payload
    };

    this.client.publish(topic, JSON.stringify(fullReq), { qos: 1 }, (err) => {
      if (err) console.error(`[MQTT] ${getLogTime()} 發送失敗: ${topic}`, err);
    });
  }

  ngOnDestroy() {
    this.disconnect();
  }
}
